import './ListPage.scss';
import { Button, Icon, SimpleNumberCard, Checkbox } from '@design-system/index';
import {
    ArchiveLine,
    ArchiveFilled,
    UnarchiveFilled,
    UnarchiveLine,
    CustomersListLine,
} from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef } from '@tanstack/react-table';
import { useListPageVM } from './useListPageVM';
import { Permission } from '@components/Permission/Permission';
import { MemberValidationTable } from '@services/member/member-validation/member-validation.types';
import { formatDate } from '@utils/dates';

export default function ListPage() {
    const {
        handleToggleArchiveMemberValidations,
        handleMetadata,
        navigate,
        columnHelper,
        isArchived,
        setIsArchived,
        totalMemberValidations,
        mutationHook,
        isLoading,
        needReload,
        user
    } = useListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            meta: {
                role: 'admin'
            },
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor((row) => row.cosemNumber, {
            id: 'cosemNumber',
            header: 'N°COSEM',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.lastname, {
            header: 'Nom',
            id: 'lastname',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor('firstname', {
            header: 'Prénom',
            id: 'firstname',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor('birthDate', {
            header: 'Date de naissance',
            id: 'birthDate',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{formatDate(info.getValue())}</>
        }),
    ] as ColumnDef<MemberValidationTable>[];

    return (
        <div className="members-table-container">
            <div className="members-table-container-header">
                <div className="members-table-container-header-left">
                    <SimpleNumberCard
                        labelForSingle={isArchived ? 'Validation traitée' : 'Validation en attente'}
                        labelForMultiple={isArchived ? 'Validations traitées' : 'Validations en attente'}
                        value={totalMemberValidations}
                    />
                    {/* <LargeIconButton
                        icon={<AddDashed />}
                        label="Nouvelle information"
                        onClick={() => navigate('/members-management/flash-infos/add')}
                    /> */}
                </div>
                <Permission role="admin">
                    <Button variant="secondary" onClick={() => setIsArchived(!isArchived)}>
                        {isArchived ? 'En attente' : 'Traitées'}
                    </Button>
                </Permission>
            </div>
            <MutationTable
                key={isArchived ? 'archived' : 'active'}
                columns={columns}
                forceLoading={isLoading}
                selectable={user?.role === 'admin'}
                needReload={needReload}
                mutationHook={mutationHook}
                onRowClick={(row) => navigate(`/products-management/members-validation/${row.id}/edit`)}
                defaultSorting={{
                    field: 'lastname',
                    direction: 'desc'
                }}
                deleting={{
                    enabled: isArchived,
                    entity: 'member-validation'
                }}
                headerTitle={isArchived ? 'Liste des validations traitées' : 'Liste des validations en attente'}
                headerIcon={<CustomersListLine />}
                onMetadata={handleMetadata}
                footer={{
                    actions: [
                        {
                            role: 'admin',
                            label: isArchived ? 'Désarchiver' : 'Archiver',
                            icon: <Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />,
                            hoverIcon: <Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />,
                            onClick: (lines: MemberValidationTable[]) => handleToggleArchiveMemberValidations(lines, !isArchived)
                        }
                    ]
                }}
            />
        </div>
    );
}
